import React from 'react';
import PropTypes from 'prop-types';
import { formatPrice, toNumber } from '../priceFormatting';
import { seriesPassProductShape } from '../productShape';

export default function PriceSummary({ product, currency, quantity, appliedGiftCards, amountDue }) {
  const breakdown = product && product.priceBreakdown;
  if (!breakdown || breakdown.total == null) return null;

  const units = Math.max(1, Number(quantity) || 1);

  const byQuantity = product.priceBreakdownsByQuantity;
  if (byQuantity == null && units > 1) return null;

  const quoted = byQuantity && byQuantity[units];
  const line = (key) => (quoted ? toNumber(quoted[key]) : toNumber(breakdown[key]) * units);

  const discount = line('discount');
  const surcharges = (breakdown.surcharges || []).map((surcharge, index) => {
    const quotedSurcharge = quoted && quoted.surcharges && quoted.surcharges[index];
    return {
      name: surcharge.name,
      unitAmount: surcharge.amount,
      amount: quotedSurcharge ? toNumber(quotedSurcharge.amount) : toNumber(surcharge.amount) * units,
    };
  });
  const fees = line('fees');
  const taxes = line('taxes');

  return (
    <dl className="series-pass-summary" data-testid="series-pass-summary">
      <div className="series-pass-summary-row">
        <dt>
          {units > 1
            ? `Subtotal (${units} × ${formatPrice(breakdown.subtotal, currency)})`
            : 'Subtotal'}
        </dt>
        <dd data-testid="series-pass-summary-subtotal">
          {formatPrice(line('subtotal'), currency)}
        </dd>
      </div>
      {surcharges.map((surcharge, index) => (
        <div className="series-pass-summary-row" key={`${surcharge.name}-${index}`}>
          <dt>
            {units > 1
              ? `${surcharge.name} (${units} × ${formatPrice(surcharge.unitAmount, currency)})`
              : surcharge.name}
          </dt>
          <dd data-testid={`series-pass-summary-surcharge-${index}`}>
            {formatPrice(surcharge.amount, currency)}
          </dd>
        </div>
      ))}
      {discount > 0 && (
        <div className="series-pass-summary-row">
          <dt>Discount</dt>
          <dd data-testid="series-pass-summary-discount">
            {`-${formatPrice(discount, currency)}`}
          </dd>
        </div>
      )}
      {fees > 0 && (
        <div className="series-pass-summary-row">
          <dt>Fees</dt>
          <dd data-testid="series-pass-summary-fees">{formatPrice(fees, currency)}</dd>
        </div>
      )}
      {taxes > 0 && (
        <div className="series-pass-summary-row">
          <dt>Taxes</dt>
          <dd data-testid="series-pass-summary-taxes">{formatPrice(taxes, currency)}</dd>
        </div>
      )}
      <div className="series-pass-summary-row series-pass-summary-total">
        <dt>Total</dt>
        <dd data-testid="series-pass-summary-total">
          {formatPrice(line('total'), currency)}
        </dd>
      </div>
      {appliedGiftCards.map((card) => (
        <div className="series-pass-summary-row" key={card.id} data-testid="series-pass-summary-gift-card">
          <dt>Gift card •••• {card.code_last4}</dt>
          <dd>{`-${formatPrice(card.amount_cents / 100, currency)}`}</dd>
        </div>
      ))}
      {appliedGiftCards.length > 0 && amountDue != null && (
        <div className="series-pass-summary-row series-pass-summary-total">
          <dt>Due now</dt>
          <dd data-testid="series-pass-summary-due">{formatPrice(amountDue, currency)}</dd>
        </div>
      )}
    </dl>
  );
}

PriceSummary.propTypes = {
  product: seriesPassProductShape,
  currency: PropTypes.string,
  quantity: PropTypes.number,
  appliedGiftCards: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      code_last4: PropTypes.string.isRequired,
      amount_cents: PropTypes.number.isRequired,
    })
  ),
  amountDue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

PriceSummary.defaultProps = { product: null, currency: 'USD', quantity: 1, appliedGiftCards: [], amountDue: null };
